import { NgIf, NgForOf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  Output,
  signal,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR
} from '@angular/forms';

@Component({
  selector: 'app-color-picker',
  imports: [FormsModule, NgIf, NgForOf],
  templateUrl: './color-picker.component.html',
  styleUrls: ['./color-picker.component.scss'],
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ColorPickerComponent),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None
})
export class ColorPickerComponent
  implements AfterViewInit, OnChanges, ControlValueAccessor
{
  @Output() colorSelected = new EventEmitter<string>();
  @Output() hexChanged = new EventEmitter<string>();
  @Output() closePopup = new EventEmitter<void>();
  @ViewChild('hueRing') hueRing!: ElementRef<HTMLElement>;
  @ViewChild('satVal') satVal!: ElementRef<HTMLElement>;
  hue = signal(0);
  saturation = signal(100);
  value = signal(100);
  format = signal<'hex' | 'rgb'>('hex');
  selectedPreset = signal<string | null>(null);
  isDragging = false;
  dragType: 'hue' | 'sat' | null = null;
  disabled = false;
  currentColorSignal = signal('#FF0000');
  // Computed values
  currentColor = computed(() => {
    return this.hsvToHex(this.hue(), this.saturation(), this.value());
  });

  presets = computed(() => {
    const currentHue = this.hue();
    const currentSaturation = this.saturation();
    return Array.from({ length: 8 }, (_, i) => {
      const value = 100 - i * 12;
      return this.hsvToHex(currentHue, currentSaturation, value);
    });
  });

  rgbValues = computed(() =>
    this.hsvToRgb(this.hue(), this.saturation(), this.value())
  );
  isLightColor = computed(() => {
    const rgb = this.rgbValues();
    return this.getRelativeLuminance(rgb.r, rgb.g, rgb.b) > 0.5;
  });

  constructor() {}

  @Input() set hexColor(value: string) {
    if (value) {
      this.updateFromHex(value);
    }
  }

  // ControlValueAccessor implementation
  writeValue(color: string): void {
    if (color) {
      this.updateFromHex(color);
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngAfterViewInit() {
    this.updateSatValBackground();
  }

  ngOnChanges() {
    this.updateSatValBackground();
  }

  // Drag handling
  startDrag(event: MouseEvent | TouchEvent, type: 'hue' | 'sat') {
    if (this.disabled) {
      return;
    }

    event.preventDefault();
    this.isDragging = true;
    this.dragType = type;
    this.handleDrag(event);
    this.onTouched();
  }

  @HostListener('document:mousemove', ['$event'])
  @HostListener('document:touchmove', ['$event'])
  handleDrag(event: MouseEvent | TouchEvent) {
    if (!this.isDragging || !this.dragType || this.disabled) {
      return;
    }

    if (this.dragType === 'hue') {
      this.updateHue(event);
    } else {
      this.updateSatVal(event);
    }
  }

  @HostListener('document:mouseup')
  @HostListener('document:touchend')
  @HostListener('document:mouseleave')
  @HostListener('window:blur')
  stopDrag() {
    if (this.isDragging) {
      this.isDragging = false;
      this.dragType = null;
    }
  }

  updateFromHex(hex: string, isPreset = false) {
    if (this.disabled) {
      return;
    }

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      const rgb = {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      };
      const hsv = this.rgbToHsv(rgb.r, rgb.g, rgb.b);
      this.hue.set(hsv.h);
      this.saturation.set(hsv.s);
      this.value.set(hsv.v);
      const normalizedHex = hex.startsWith('#') ? hex : `#${hex}`;
      const finalHex = normalizedHex.toUpperCase();
      this.hexChanged.emit(finalHex);

      if (isPreset) {
        this.selectedPreset.set(finalHex);
      } else {
        this.selectedPreset.set(null);
      }
    }
  }

  updateSatValBackground() {
    const hue = this.hue();
    if (this.satVal?.nativeElement) {
      this.satVal.nativeElement.style.background = `linear-gradient(to right, white, hsl(${hue}, 100%, 50%)), linear-gradient(to bottom, rgba(0, 0, 0, 0), black)`;
      this.satVal.nativeElement.style.backgroundBlendMode = 'multiply';
    }
  }

  setFormat(newFormat: 'hex' | 'rgb') {
    this.format.set(newFormat);
  }

  updateRgb(component: 'r' | 'g' | 'b', value: number) {
    const rgb = this.rgbValues();
    rgb[component] = Math.max(0, Math.min(255, value));
    const hex = this.rgbToHex(rgb.r, rgb.g, rgb.b);
    this.updateFromHex(hex);
  }

  getRelativeLuminance(r: number, g: number, b: number): number {
    const [rs, gs, bs] = [r, g, b].map((val) => {
      val = val / 255;
      return val <= 0.03928
        ? val / 12.92
        : Math.pow((val + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  }

  getLuminanceForHex(hex: string): number {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      const rgb = {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      };
      return this.getRelativeLuminance(rgb.r, rgb.g, rgb.b);
    }
    return 0;
  }

  private getEventCoordinates(event: MouseEvent | TouchEvent): {
    clientX: number;
    clientY: number;
  } {
    if (event instanceof TouchEvent) {
      const touch = event.touches[0] || event.changedTouches[0];
      return { clientX: touch.clientX, clientY: touch.clientY };
    }
    return { clientX: event.clientX, clientY: event.clientY };
  }

  // ControlValueAccessor callbacks
  private onChange: (value: string) => void = () => {};

  private onTouched: () => void = () => {};

  // Input handling
  private emitColorChange(color: string) {
    this.onChange(color);
    this.onTouched();
  }

  // Color conversions
  private hsvToHex(h: number, s: number, v: number): string {
    s /= 100;
    v /= 100;
    const i = Math.floor(h / 60);
    const f = h / 60 - i;
    const p = v * (1 - s);
    const q = v * (1 - f * s);
    const t = v * (1 - (1 - f) * s);

    let r = 0,
      g = 0,
      b = 0;
    switch (i % 6) {
      case 0:
        [r, g, b] = [v, t, p];
        break;
      case 1:
        [r, g, b] = [q, v, p];
        break;
      case 2:
        [r, g, b] = [p, v, t];
        break;
      case 3:
        [r, g, b] = [p, q, v];
        break;
      case 4:
        [r, g, b] = [t, p, v];
        break;
      case 5:
        [r, g, b] = [v, p, q];
        break;
    }

    return `#${[r, g, b]
      .map((x) =>
        Math.round(x * 255)
          .toString(16)
          .padStart(2, '0')
      )
      .join('')
      .toUpperCase()}`;
  }

  private hsvToRgb(h: number, s: number, v: number) {
    s /= 100;
    v /= 100;
    const i = Math.floor(h / 60);
    const f = h / 60 - i;
    const p = v * (1 - s);
    const q = v * (1 - f * s);
    const t = v * (1 - (1 - f) * s);

    let r = 0,
      g = 0,
      b = 0;
    switch (i % 6) {
      case 0:
        [r, g, b] = [v, t, p];
        break;
      case 1:
        [r, g, b] = [q, v, p];
        break;
      case 2:
        [r, g, b] = [p, v, t];
        break;
      case 3:
        [r, g, b] = [p, q, v];
        break;
      case 4:
        [r, g, b] = [t, p, v];
        break;
      case 5:
        [r, g, b] = [v, p, q];
        break;
    }

    return {
      r: Math.round(r * 255),
      g: Math.round(g * 255),
      b: Math.round(b * 255)
    };
  }

  // Preset generation
  private generatePresets(): string[] {
    const currentHue = this.hue();
    return Array.from({ length: 8 }, (_, i) => {
      const saturation = 100;
      const value = 100 - i * 12;
      return this.hsvToHex(currentHue, saturation, value);
    });
  }

  private updateHue(event: MouseEvent | TouchEvent) {
    const { clientX, clientY } = this.getEventCoordinates(event);
    const rect = this.hueRing.nativeElement.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const x = clientX - centerX;
    const y = clientY - centerY;

    const distance = Math.sqrt(x * x + y * y);

    if (distance >= 82 && distance <= 105) {
      let angle = Math.atan2(y, x);
      angle = ((angle * 180) / Math.PI + 90 + 360) % 360;

      this.hue.set(angle);
      this.updateSatValBackground();
      const color = this.currentColor();
      this.hexChanged.emit(color);
      this.selectedPreset.set(null);
    }
  }

  private updateSatVal(event: MouseEvent | TouchEvent) {
    const { clientX, clientY } = this.getEventCoordinates(event);
    const rect = this.satVal.nativeElement.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    this.saturation.set(Math.max(0, Math.min(100, (x / rect.width) * 100)));
    this.value.set(Math.max(0, Math.min(100, 100 - (y / rect.height) * 100)));
    const color = this.currentColor();
    this.hexChanged.emit(color);
    this.selectedPreset.set(null);
  }

  private rgbToHsv(r: number, g: number, b: number) {
    ((r /= 255), (g /= 255), (b /= 255));
    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    const d = max - min;
    let h = 0;

    if (d !== 0) {
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h *= 60;
    }

    const s = max === 0 ? 0 : d / max;
    const v = max;

    return {
      h: Math.round(h),
      s: Math.round(s * 100),
      v: Math.round(v * 100)
    };
  }

  private rgbToHex(r: number, g: number, b: number): string {
    const toHex = (n: number) =>
      Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
}
